import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { api } from '../api/client';
import { useAsync, Async } from '../components/Async';
import { Flag } from '../components/TeamBadge';
import { teamName } from '../lib/flags';
import { H2, Muted, BackLink } from '../components/ui';

const Champ = styled.div`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.gold}, #b9820a);
  color: #1a1300;
  border-radius: ${({ theme }) => theme.radius};
  padding: 16px;
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  .t { font-size: 0.85rem; font-weight: 700; opacity: 0.85; }
  .n { font-size: 1.3rem; font-weight: 800; }
  .h { font-size: 0.82rem; opacity: 0.85; }
`;

const Boot = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1.5px solid ${({ theme }) => theme.colors.gold};
  border-radius: ${({ theme }) => theme.radius};
  padding: 12px 14px;
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  .t { font-size: 0.8rem; color: ${({ theme }) => theme.colors.textMuted}; }
  .n { font-weight: 800; }
  .g { margin-left: auto; font-weight: 800; color: ${({ theme }) => theme.colors.accent}; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
`;
const Kpi = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1.5px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius};
  padding: 14px;
  .n { font-size: 1.6rem; font-weight: 800; color: ${({ theme }) => theme.colors.accent}; }
  .l { font-size: 0.8rem; color: ${({ theme }) => theme.colors.textMuted}; }
`;
const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  padding: 8px 12px;
  margin-bottom: 6px;
  .pos { width: 22px; font-weight: 800; color: ${({ theme }) => theme.colors.textMuted}; text-align: center; }
  .nm { flex: 1; font-weight: 600; }
  .g { font-weight: 800; color: ${({ theme }) => theme.colors.accent}; }
`;

const medal = (i: number) => (i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : String(i + 1));
const num = (n: number | null) => (n == null ? '–' : n.toLocaleString('es-AR'));

export function HistoricoYear() {
  const { year = '' } = useParams();
  const navigate = useNavigate();
  const state = useAsync(() => api.historyYear(year), [year], `/history/${year}`);

  return (
    <>
      <BackLink onClick={() => navigate('/historico')}>← Histórico</BackLink>
      <Async state={state}>
        {({ summary: s, topScorers }) => (
          <>
            <h2 style={{ marginTop: 0 }}>Mundial {s.year}</h2>

            {s.champion && (
              <Champ>
                🏆 <Flag team={s.champion} size={32} />
                <span>
                  <div className="t">Campeón</div>
                  <div className="n">{teamName(s.champion)}</div>
                  <div className="h">Anfitrión: {s.host}</div>
                </span>
              </Champ>
            )}

            {s.goldenBoot && (
              <Boot>
                👟 <Flag team={s.goldenBoot.team} size={20} />
                <span>
                  <div className="t">Bota de Oro</div>
                  <div className="n">{s.goldenBoot.name}</div>
                </span>
                <span className="g">{s.goldenBoot.goals ?? '–'} {s.goldenBoot.goals === 1 ? 'gol' : 'goles'}</span>
              </Boot>
            )}

            <Grid>
              <Kpi><div className="n">{s.teams}</div><div className="l">equipos</div></Kpi>
              <Kpi><div className="n">{s.matches}</div><div className="l">partidos</div></Kpi>
              <Kpi><div className="n">{s.goals}</div><div className="l">goles</div></Kpi>
              <Kpi><div className="n">{s.avg}</div><div className="l">promedio gol/partido</div></Kpi>
              <Kpi><div className="n">{num(s.attendanceAvg)}</div><div className="l">público promedio</div></Kpi>
              <Kpi><div className="n">{num(s.attendanceTotal)}</div><div className="l">público total</div></Kpi>
              <Kpi><div className="n">{s.yellowCards}</div><div className="l">🟨 amarillas</div></Kpi>
              <Kpi><div className="n">{s.redCards}</div><div className="l">🟥 rojas</div></Kpi>
              <Kpi><div className="n">{s.penalties}</div><div className="l">goles de penal</div></Kpi>
              <Kpi><div className="n">{s.shootouts}</div><div className="l">tandas de penales</div></Kpi>
              <Kpi><div className="n">{s.scorelessDraws}</div><div className="l">empates 0-0</div></Kpi>
              <Kpi><div className="n">{s.hatTricks}</div><div className="l">hat-tricks</div></Kpi>
            </Grid>

            {s.biggestWin && (
              <>
                <H2>💥 Mayor goleada</H2>
                <Row>
                  <Flag team={s.biggestWin.team1} size={20} />
                  <span className="nm">
                    {teamName(s.biggestWin.team1)} {s.biggestWin.ft[0]}–{s.biggestWin.ft[1]}{' '}
                    {teamName(s.biggestWin.team2)}
                  </span>
                  <Flag team={s.biggestWin.team2} size={20} />
                </Row>
              </>
            )}

            <H2>🥇 Goleadores</H2>
            {topScorers.map((sc, i) => (
              <Row key={sc.team + sc.name + i}>
                <span className="pos">{medal(i)}</span>
                <Flag team={sc.team} size={20} />
                <span className="nm">
                  {sc.name}{' '}
                  <Muted as="span" style={{ fontSize: '0.8rem' }}>· {teamName(sc.team)}</Muted>
                </span>
                <span className="g">{sc.goals}</span>
              </Row>
            ))}
            {topScorers.length === 0 && <Muted>Sin datos de goleadores.</Muted>}
          </>
        )}
      </Async>
    </>
  );
}
